import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Eye, Ban, CheckCircle, DollarSign, X, User, Wallet, Gamepad2, ArrowLeft, History, Shield, Plus, Minus, RefreshCw, ArrowUp, ArrowDown, BarChart3, MessageSquare, Send } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/ui/Modal';
import PlayerDetailPage from './PlayerDetailPage';
import PlayerPortfolioPage from './PlayerPortfolioPage';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import LiveBadge from '../../components/LiveBadge';

// Admin Game Accounts - Load/Redeem/Balance per player
function AdminGameAccounts({ userId, accounts, onRefresh }) {
  const [processing, setProcessing] = useState({});
  const [loadAmounts, setLoadAmounts] = useState({});
  const [showLoadModal, setShowLoadModal] = useState(null);

  const handleCheckBalance = async (gameId) => {
    setProcessing(p => ({ ...p, [`bal_${gameId}`]: true }));
    try {
      const res = await api.get(`/api/admin/users/${userId}/games/${gameId}/balance`);
      toast.success(`Balance: $${res.data.balance}`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to check balance'); }
    finally { setProcessing(p => ({ ...p, [`bal_${gameId}`]: false })); }
  };

  const handleLoad = async (gameId) => {
    const amt = parseFloat(loadAmounts[gameId]);
    if (!amt || amt <= 0) { toast.error('Enter valid $ amount'); return; }
    setProcessing(p => ({ ...p, [`load_${gameId}`]: true }));
    try {
      const res = await api.post(`/api/admin/users/${userId}/games/${gameId}/load`, { amount: amt });
      toast.success(`Loaded $${amt.toFixed(2)} into game`);
      setShowLoadModal(null);
      setLoadAmounts(p => ({ ...p, [gameId]: '' }));
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Load failed'); }
    finally { setProcessing(p => ({ ...p, [`load_${gameId}`]: false })); }
  };

  const handleRedeem = async (gameId, gameName) => {
    if (!window.confirm(`Redeem ALL credits from ${gameName} for this player? This will withdraw the full game balance back to their cash wallet.`)) return;
    setProcessing(p => ({ ...p, [`redeem_${gameId}`]: true }));
    try {
      const res = await api.post(`/api/admin/users/${userId}/games/${gameId}/redeem`);
      toast.success(`Redeemed game credits successfully`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Redeem failed'); }
    finally { setProcessing(p => ({ ...p, [`redeem_${gameId}`]: false })); }
  };

  return (
    <div className="glass-card rounded-xl p-5" data-testid="admin-game-accounts">
      <h3 className="font-semibold mb-3 flex items-center gap-2"><Gamepad2 className="w-4 h-4 text-[#D4AF37]" /> Game Accounts ({accounts.length})</h3>
      {accounts.length === 0 ? <p className="text-gray-500 text-sm">No game accounts yet</p> : (
        <div className="space-y-3">
          {accounts.map(a => (
            <div key={a.id} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid={`admin-game-${a.game_id || a.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-sm text-white">{a.game_name || a.game_code}</h4>
                  <p className="text-[10px] text-gray-500">User: {a.username} | Pass: {a.password} | ID: {a.provider_account_id || 'N/A'}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-[#D4AF37]">${(Number(a.last_known_balance) || 0).toFixed(2)}</p>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handleCheckBalance(a.game_id)} disabled={processing[`bal_${a.game_id}`]}
                  className="flex-1 bg-white/5 border border-white/10 text-xs h-8" data-testid={`admin-balance-${a.game_id}`}>
                  {processing[`bal_${a.game_id}`] ? <div className="spinner w-3 h-3" /> : <><RefreshCw className="w-3 h-3 mr-1" /> Check Balance</>}
                </Button>
                <Button size="sm" onClick={() => setShowLoadModal(showLoadModal === a.game_id ? null : a.game_id)}
                  className="flex-1 bg-purple-600/80 hover:bg-purple-600 text-xs h-8" data-testid={`admin-load-${a.game_id}`}>
                  <ArrowDown className="w-3 h-3 mr-1" /> Load Credits
                </Button>
                <Button size="sm" onClick={() => handleRedeem(a.game_id, a.game_name || a.game_code)} disabled={processing[`redeem_${a.game_id}`]}
                  className="flex-1 bg-green-600/80 hover:bg-green-600 text-xs h-8" data-testid={`admin-redeem-${a.game_id}`}>
                  {processing[`redeem_${a.game_id}`] ? <div className="spinner w-3 h-3" /> : <><ArrowUp className="w-3 h-3 mr-1" /> Withdraw</>}
                </Button>
              </div>
              {/* Load Amount Input */}
              {showLoadModal === a.game_id && (
                <div className="mt-3 p-3 rounded-lg border border-purple-500/30" style={{ background: 'rgba(108,92,231,0.05)' }}>
                  <p className="text-xs text-gray-400 mb-2">Load $ into {a.game_name || a.game_code}</p>
                  <div className="flex gap-2 mb-2">
                    {[5, 10, 25, 50, 100].map(v => (
                      <button key={v} onClick={() => setLoadAmounts(p => ({ ...p, [a.game_id]: v.toString() }))}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition ${loadAmounts[a.game_id] === v.toString() ? 'bg-purple-500/20 border-purple-500 text-purple-400' : 'border-white/10 text-gray-400 hover:bg-white/5'}`}>${v}</button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input type="number" step="0.01" value={loadAmounts[a.game_id] || ''} onChange={(e) => setLoadAmounts(p => ({ ...p, [a.game_id]: e.target.value }))}
                      placeholder="USD amount" className="h-8 text-sm bg-[#0B0E14] border-white/10 flex-1" data-testid={`admin-load-input-${a.game_id}`} />
                    <Button size="sm" onClick={() => handleLoad(a.game_id)} disabled={processing[`load_${a.game_id}`]}
                      className="bg-purple-600 h-8 px-4" data-testid={`admin-load-btn-${a.game_id}`}>
                      {processing[`load_${a.game_id}`] ? <div className="spinner w-3 h-3" /> : 'Load'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlayersPage() {
  const { user: adminUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [playerDetail, setPlayerDetail] = useState(null);
  const [playerWallet, setPlayerWallet] = useState(null);
  const [playerAccounts, setPlayerAccounts] = useState([]);
  const [playerTxns, setPlayerTxns] = useState([]);
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustType, setAdjustType] = useState('add');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [detailTab, setDetailTab] = useState('overview');
  const [kpiPeriod, setKpiPeriod] = useState('week');
  const [kpis, setKpis] = useState(null);
  const [messagePlayer, setMessagePlayer] = useState(null); // {id, username} for admin-initiated chat

  // URL params: ?filter=new&period=day|week|month — drill-down from Insights KPI
  const urlFilter = searchParams.get('filter') || '';
  const urlPeriod = searchParams.get('period') || '';
  const createdAfterIso = useMemo(() => {
    if (urlFilter !== 'new') return null;
    const now = new Date();
    const map = { day: 1, week: 7, month: 30, '90d': 90 };
    const days = map[urlPeriod] ?? 7;
    const d = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return d.toISOString();
  }, [urlFilter, urlPeriod]);

  const periodLabel = ({ day: 'last 24 hours', week: 'last 7 days', month: 'last 30 days', '90d': 'last 90 days' })[urlPeriod] || 'last 7 days';

  useEffect(() => { fetchPlayers(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { fetchPlayers(); /* eslint-disable-next-line */ }, [createdAfterIso]);
  useEffect(() => { fetchKpis(); /* eslint-disable-next-line */ }, [kpiPeriod]);

  // Live auto-sync: refresh the player list + KPIs every 5s while the
  // admin has the page open (paused when tab hidden or a player is
  // drilled into). Silent — no blink, no loading spinner flash.
  useAutoRefresh(() => {
    if (selectedPlayer || selectedPortfolio) return;
    fetchPlayers({ silent: true });
    fetchKpis();
  }, 5000, !selectedPlayer && !selectedPortfolio);

  const fetchKpis = async () => {
    try {
      const res = await api.get(`/api/admin/users/kpis/summary?period=${kpiPeriod}`);
      setKpis(res.data);
    } catch { /* non-fatal */ }
  };

  const fetchPlayers = async (opts = {}) => {
    const { silent = false } = opts;
    try {
      // Always filter to player roles only — operators shouldn't see
      // themselves or other staff in this list.
      const params = new URLSearchParams({ players_only: 'true', limit: '200' });
      if (search) params.set('search', search);
      if (createdAfterIso) params.set('created_after', createdAfterIso);
      const res = await api.get(`/api/admin/users?${params.toString()}`);
      setPlayers(res.data.users || res.data.items || []);
    } catch { if (!silent) toast.error('Failed to load players'); }
    finally { if (!silent) setLoading(false); }
  };

  const clearNewFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('filter'); next.delete('period');
    setSearchParams(next);
  };

  const openPlayer = useCallback(async (userId) => {
    setSelectedPlayer(userId);
    setDetailTab('overview');
    try {
      const [userRes, walletRes, accRes, txnRes] = await Promise.all([
        api.get(`/api/admin/users/${userId}`),
        api.get(`/api/admin/users/${userId}/wallet`).catch(() => ({ data: {} })),
        api.get(`/api/admin/users/${userId}/game-accounts`).catch(() => ({ data: { accounts: [] } })),
        api.get(`/api/admin/users/${userId}/transactions?limit=50`).catch(() => ({ data: { transactions: [] } })),
      ]);
      setPlayerDetail(userRes.data);
      setPlayerWallet(walletRes.data);
      setPlayerAccounts(accRes.data.accounts || accRes.data.items || []);
      setPlayerTxns(txnRes.data.transactions || txnRes.data.items || []);
    } catch { toast.error('Failed to load player details'); }
  }, []);

  const handleAdjust = async () => {
    if (!adjustAmount || !adjustReason) { toast.error('Amount and reason required'); return; }
    setAdjusting(true);
    try {
      const amt = parseFloat(adjustAmount) * (adjustType === 'subtract' ? -1 : 1);
      await api.post(`/api/admin/users/${selectedPlayer}/adjust-wallet`, {
        amount: amt, adjust_type: 'balance', reason: adjustReason,
      });
      toast.success(`Wallet ${adjustType === 'add' ? 'credited' : 'debited'} successfully`);
      setShowAdjust(false);
      setAdjustAmount(''); setAdjustReason('');
      openPlayer(selectedPlayer);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to adjust'); }
    finally { setAdjusting(false); }
  };

  const toggleDisable = async (player) => {
    try {
      await api.post(`/api/admin/users/${player.id}/status`, {
        status: player.disabled ? 'active' : 'disabled',
        reason: player.disabled ? 'Enabled from players admin panel' : 'Disabled from players admin panel',
      });
      toast.success(player.disabled ? 'Player enabled' : 'Player disabled');
      fetchPlayers();
      if (selectedPlayer === player.id) openPlayer(player.id);
    } catch { toast.error('Failed to update status'); }
  };

  const forceLogout = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/force-logout`);
      toast.success('Player sessions invalidated');
    } catch { toast.error('Failed'); }
  };

  const filteredPlayers = search ? players : players;

  if (selectedPortfolio) {
    return <PlayerPortfolioPage playerId={selectedPortfolio} onBack={() => setSelectedPortfolio(null)} />;
  }

  if (selectedPlayer) {
    return <PlayerDetailPage playerId={selectedPlayer} onBack={() => { setSelectedPlayer(null); setPlayerDetail(null); }} />;
  }

  return (
    <div className="space-y-4 animate-fade-in-up" data-testid="players-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-3">
            Players <LiveBadge />
          </h2>
          <p className="text-sm text-gray-400">
            {players.length} {urlFilter === 'new' ? `new players · ${periodLabel}` : 'players'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 mr-1">KPI period:</span>
          {['today', 'yesterday', 'week', 'month'].map(p => (
            <button
              key={p}
              onClick={() => setKpiPeriod(p)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${
                kpiPeriod === p
                  ? 'bg-[#D4AF37] text-black'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`players-kpi-period-${p}`}
            >
              {p === 'today' ? 'Today' : p === 'yesterday' ? 'Yesterday' : p === 'week' ? 'This Week' : 'This Month'}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="players-kpi-row">
        <KPITile label="Total Players" value={(kpis?.total_players ?? '—').toLocaleString?.() ?? kpis?.total_players ?? '—'} color="#D4AF37" />
        <KPITile label="New Sign-ups" value={(kpis?.new_signups ?? 0).toLocaleString()} color="#3B82F6" hint="in period" />
        <KPITile label="Active Players" value={(kpis?.active_players ?? 0).toLocaleString()} color="#22c55e" hint="in period" />
        <KPITile label="Cash In Wallets" value={`$${Number(kpis?.total_cash_usd ?? kpis?.total_cash_sc ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})}`} color="#f59e0b" />
        <KPITile label="Deposits" value={`$${Number(kpis?.deposits_usd ?? kpis?.deposits_sc ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})}`} color="#8b5cf6" hint={`${kpis?.deposit_count ?? 0} approvals`} />
        <KPITile label="Withdrawals" value={`$${Number(kpis?.withdrawals_usd ?? kpis?.withdrawals_sc ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})}`} color="#ef4444" hint={`${kpis?.withdrawal_count ?? 0} approvals`} />
      </div>

      {urlFilter === 'new' && (
        <div
          className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs"
          style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.25)' }}
          data-testid="new-signups-filter-pill"
        >
          <span className="text-blue-300 font-semibold">Filter:</span>
          <span className="text-white">New sign-ups · {periodLabel}</span>
          <button
            onClick={clearNewFilter}
            className="ml-auto text-blue-300 hover:text-white underline"
            data-testid="clear-new-filter"
          >
            Clear filter
          </button>
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input value={search} onChange={e => { setSearch(e.target.value); }} onKeyDown={e => e.key === 'Enter' && fetchPlayers()}
            placeholder="Search by username or email..." className="pl-10 bg-[#131822] border-white/10" data-testid="player-search" />
        </div>
      </div>

      <div className="glass-card rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="players-table">
          <thead><tr className="border-b border-white/10 text-gray-400 text-xs">
            <th className="text-left py-3 px-4">Player</th>
            <th className="text-left py-3 px-4">Email</th>
            <th className="text-right py-3 px-4">Cash</th>
            <th className="text-right py-3 px-4">FP</th>
            <th className="text-right py-3 px-4">Deposited</th>
            <th className="text-right py-3 px-4">Withdrawn</th>
            <th className="text-left py-3 px-4">Tier</th>
            <th className="text-left py-3 px-4">Status</th>
            <th className="text-left py-3 px-4">Joined</th>
            <th className="text-right py-3 px-4">Actions</th>
          </tr></thead>
          <tbody>
            {filteredPlayers.map(p => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => openPlayer(p.id)}>
                <td className="py-3 px-4 font-medium">{p.username}</td>
                <td className="py-3 px-4 text-gray-400">{p.email}</td>
                <td className="py-3 px-4 text-right text-[#D4AF37] font-medium">${(Number(p.wallet_balance) || 0).toLocaleString(undefined,{maximumFractionDigits:2})}</td>
                <td className="py-3 px-4 text-right text-indigo-300 font-medium">{(Number(p.wallet_play_credits) || 0).toLocaleString(undefined,{maximumFractionDigits:2})}</td>
                <td className="py-3 px-4 text-right text-green-400 font-medium">${(Number(p.total_deposits) || 0).toLocaleString(undefined,{maximumFractionDigits:2})}</td>
                <td className="py-3 px-4 text-right text-red-400 font-medium">${(Number(p.total_withdrawals) || 0).toLocaleString(undefined,{maximumFractionDigits:2})}</td>
                <td className="py-3 px-4"><span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37]">{(p.tier || 'starter').toUpperCase()}</span></td>
                <td className="py-3 px-4"><span className={`text-[10px] px-2 py-0.5 rounded-full ${p.disabled ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>{p.disabled ? 'Disabled' : 'Active'}</span></td>
                <td className="py-3 px-4 text-gray-500 text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); setMessagePlayer({ id: p.id, username: p.username }); }}
                      className="text-xs h-7 text-blue-300 hover:text-blue-200"
                      data-testid={`player-message-btn-${p.id}`}
                      title="Message player"
                    >
                      <MessageSquare className="w-3 h-3 mr-1" /> Message
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); setSelectedPortfolio(p.id); }}
                      className="text-xs h-7 text-[#D4AF37] hover:text-[#F5D76E]"
                      data-testid={`player-portfolio-btn-${p.id}`}
                      title="Open full portfolio"
                    >
                      <BarChart3 className="w-3 h-3 mr-1" /> Portfolio
                    </Button>
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openPlayer(p.id); }} className="text-xs h-7">
                      <Eye className="w-3 h-3 mr-1" /> View
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); toggleDisable(p); }}
                      className={`text-xs h-7 ${p.disabled ? 'text-green-300 hover:text-green-200' : 'text-red-300 hover:text-red-200'}`}
                      data-testid={`player-status-toggle-${p.id}`}
                      title={p.disabled ? 'Enable player login' : 'Disable player login'}
                    >
                      {p.disabled ? <CheckCircle className="w-3 h-3 mr-1" /> : <Ban className="w-3 h-3 mr-1" />}
                      {p.disabled ? 'Enable' : 'Disable'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="flex justify-center py-8"><div className="spinner" /></div>}
        {!loading && players.length === 0 && <p className="text-center text-gray-500 py-8">No players found</p>}
      </div>

      {messagePlayer && (
        <AdminMessageModal
          player={messagePlayer}
          onClose={() => setMessagePlayer(null)}
        />
      )}
    </div>
  );
}

/**
 * Admin-initiated chat — opens a support room with the player and sends
 * the first message. Uses POST /api/admin/chat/open-with-player which
 * creates the room on demand and fires a push notification so the
 * player sees it immediately.
 */
function AdminMessageModal({ player, onClose }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    const text = message.trim();
    if (!text) {
      toast.error('Type a message first');
      return;
    }
    setSending(true);
    try {
      await api.post('/api/chat/admin/open-with-player', {
        user_id: player.id,
        initial_message: text,
      });
      toast.success(`Message sent to ${player.username}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Message ${player.username}`}>
      <div className="space-y-3" data-testid="admin-message-modal">
        <p className="text-xs text-gray-400 leading-relaxed">
          Your message starts a new support chat with this player. They'll get a push
          notification and see it in the Chat tab.
        </p>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Hi! Just checking in — everything okay with your recent deposit?"
          rows={4}
          className="w-full rounded-lg bg-[#0B0E14] border border-white/10 p-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/50"
          data-testid="admin-message-textarea"
          autoFocus
        />
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={sending} data-testid="admin-message-cancel">
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={sending || !message.trim()}
            className="bg-[#D4AF37] hover:bg-[#c39d2f] text-black font-bold gap-1"
            data-testid="admin-message-send"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Sending…' : 'Send message'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-white/5">
      <span className="text-xs text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-200 truncate max-w-[200px]">{value}</span>
    </div>
  );
}

function KPITile({ label, value, color = '#D4AF37', hint = null }) {
  return (
    <div
      className="rounded-xl p-3 bg-[#131822] border"
      style={{ borderColor: `${color}33` }}
    >
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
      <div className="text-xl font-bold mt-1 truncate" style={{ color }}>{value}</div>
      {hint && <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = { pending: 'bg-yellow-500/20 text-yellow-400', approved: 'bg-green-500/20 text-green-400', completed: 'bg-green-500/20 text-green-400', rejected: 'bg-red-500/20 text-red-400' };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full ${colors[status] || 'bg-gray-500/20 text-gray-400'}`}>{status}</span>;
}
